import { inject, Injectable } from '@angular/core';

import { LlmService } from './llm.service';

/**
 * Agentic search over one property's guidebook.
 *
 * Two layers: `rankSections` is an instant, local keyword ranker used to filter the
 * page while the answer is being written; `answer` sends the (small) guidebook to the
 * chat-api autosuggest endpoint and asks for a grounded, cited answer. The corpus is a
 * few dozen sections, so the whole thing fits in one prompt and nothing needs an
 * embedding index — local or hosted.
 */

export interface SearchableSection {
  id: string;
  title: string;
  audience: 'guest' | 'internal';
  text: string;
}

export interface RankedSection {
  id: string;
  score: number;
}

export interface GuidebookAnswer {
  text: string;
  sourceIds: string[];
  /** False when the model says the guidebook doesn't cover the question. */
  found: boolean;
}

const STOPWORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'to', 'of', 'in', 'on', 'for', 'is', 'are', 'do', 'does', 'did', 'how',
  'what', 'where', 'when', 'which', 'who', 'why', 'i', 'we', 'you', 'it', 'this', 'that', 'these', 'those',
  'with', 'can', 'could', 'would', 'should', 'at', 'be', 'my', 'our', 'their', 'your', 'if', 'need', 'needs',
  'want', 'wants', 'know', 'about', 'there', 'any', 'get', 'me', 'please', 'have', 'has', 'was', 'were',
  'so', 'up', 'out', 'from', 'by', 'as', 'into', 'not', 'no', 'just', 'like', 'ok', 'okay', 'hey', 'um',
  'guest', 'guests', 'property', 'tell', 'says', 'say', 'something', 'anything', 'here',
]);

const MAX_CONTEXT_CHARS = 28000;
const MAX_SECTION_CHARS = 3000;

// Crude suffix stripping so "sprinklers" finds "sprinkler" and "resetting" finds "reset".
function stem(token: string): string {
  if (token.length > 5 && token.endsWith('ing')) { return token.slice(0, -3); }
  if (token.length > 4 && token.endsWith('ed')) { return token.slice(0, -2); }
  if (token.length > 3 && token.endsWith('es')) { return token.slice(0, -2); }
  if (token.length > 3 && token.endsWith('s') && !token.endsWith('ss')) { return token.slice(0, -1); }
  return token;
}

export function tokenize(text: string): string[] {
  return (text || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(token => token.length > 1 && !STOPWORDS.has(token))
    .map(stem);
}

export function rankSections(query: string, sections: SearchableSection[]): RankedSection[] {
  const terms = Array.from(new Set(tokenize(query)));
  if (!terms.length) { return []; }
  const phrase = (query || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
  const ranked: RankedSection[] = [];
  for (const section of sections) {
    const titleTokens = tokenize(section.title);
    const bodyTokens = tokenize(section.text);
    const bodyCounts = new Map<string, number>();
    for (const token of bodyTokens) {
      bodyCounts.set(token, (bodyCounts.get(token) || 0) + 1);
    }
    let score = 0;
    for (const term of terms) {
      if (titleTokens.includes(term)) { score += 4; }
      score += Math.min(bodyCounts.get(term) || 0, 3);
      if (term.length >= 4 && !bodyCounts.has(term)) {
        const near = bodyTokens.some(token => token.startsWith(term) || term.startsWith(token) && token.length >= 4);
        if (near) { score += 0.5; }
      }
    }
    if (phrase.length > 3 && `${section.title} ${section.text}`.toLowerCase().includes(phrase)) {
      score += 3;
    }
    if (score > 0) {
      ranked.push({ id: section.id, score });
    }
  }
  return ranked.sort((a, b) => b.score - a.score);
}

/** Orders sections best-match first and trims them to fit one prompt. */
export function buildContext(sections: SearchableSection[], ranked: RankedSection[]): string {
  const rankById = new Map(ranked.map((entry, index) => [entry.id, index]));
  const ordered = [...sections].sort((a, b) =>
    (rankById.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (rankById.get(b.id) ?? Number.MAX_SAFE_INTEGER)
  );
  const blocks: string[] = [];
  let used = 0;
  for (const section of ordered) {
    const body = section.text.length > MAX_SECTION_CHARS
      ? `${section.text.slice(0, MAX_SECTION_CHARS)}…`
      : section.text;
    const block = `[id=${section.id}] (${section.audience === 'guest' ? 'guest-facing' : 'internal note'}) ${section.title}\n${body}`;
    if (used + block.length > MAX_CONTEXT_CHARS) { break; }
    blocks.push(block);
    used += block.length;
  }
  return blocks.join('\n---\n');
}

@Injectable({ providedIn: 'root' })
export class GuidebookSearchService {
  private readonly llm = inject(LlmService);

  async answer(question: string, sections: SearchableSection[], ranked: RankedSection[]): Promise<GuidebookAnswer> {
    const validIds = new Set(sections.map(section => section.id));
    const response = await this.llm.suggest({
      systemPrompt: [
        'You help a short-term rental operator\'s team find information in ONE property\'s guidebook.',
        'The guidebook has guest-facing sections (shown to guests on the property website) and internal notes',
        '(playbooks, FAQs and reference only staff see). The person asking is staff, so you may use both.',
        'Answer using ONLY the sections provided. Be concise and practical: give the steps or facts directly,',
        'quote codes, times and locations exactly as written, and mention which section they come from.',
        'If the guidebook does not cover the question, say so plainly in one sentence and suggest which section',
        'would be the natural place to add it. Never invent details. Plain text only, no markdown.',
      ].join(' '),
      prompt: `Question: ${question}\n\nGuidebook sections:\n---\n${buildContext(sections, ranked)}`,
      responseSchema: {
        type: 'object',
        properties: {
          answer: { type: 'string' },
          source_ids: { type: 'array', items: { type: 'string' } },
          found: { type: 'boolean' },
        },
        required: ['answer', 'source_ids', 'found'],
      },
      maxTokens: 700,
    });
    const result = response?.result || {};
    const sourceIds: string[] = Array.isArray(result.source_ids)
      ? result.source_ids.filter((id: unknown): id is string => typeof id === 'string' && validIds.has(id))
      : [];
    return {
      text: typeof result.answer === 'string' && result.answer.trim()
        ? result.answer.trim()
        : 'TIDY could not find an answer in this guidebook.',
      sourceIds,
      found: result.found !== false && sourceIds.length > 0,
    };
  }
}
